import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect } from "react";
import { Modal } from "@/components/common";
import { Role, DEFAULT_ADMIN_ACCESS, DEFAULT_STAFF_ACCESS } from "@/types/auth.types";
import styles from "./EditUserModal.module.css";
const ROLE_OPTIONS = [
    { value: Role.ADMIN, label: "Admin" },
    { value: Role.STAFF, label: "Staff" },
];
const getRoleLabel = (role) => {
    const option = ROLE_OPTIONS.find((r) => r.value === role);
    return option?.label || role;
};
const getRoleClassName = (role) => {
    switch (role) {
        case Role.ADMIN:
            return "admin";
        case Role.STAFF:
            return "staff";
        default:
            return "";
    }
};
const CORE_ACCESS_ITEMS = [
    { key: "contacts", label: "Contacts" },
    { key: "sites", label: "Sites" },
    { key: "estimating", label: "Estimating" },
    { key: "jobs", label: "Jobs" },
];
const ADDITIONAL_ACCESS_ITEMS = [
    { key: "all", label: "All" },
    { key: "templates", label: "Templates" },
    { key: "costSettings", label: "Cost Settings" },
    { key: "forms", label: "Forms" },
    { key: "userManagement", label: "User Management" },
];
const isCoreAccessKey = (key) => CORE_ACCESS_ITEMS.some((item) => item.key === key);
export const EditUserModal = ({ isOpen, onClose, onSave, user, isSaving = false, }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [selectedRole, setSelectedRole] = useState(Role.STAFF);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [access, setAccess] = useState(DEFAULT_STAFF_ACCESS);
    // Reset state when user changes
    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setEmail(user.email || "");
            setSelectedRole(user.role);
            // Show the user's actual saved permissions, or defaults if none exist
            setAccess(user.access || (user.role === Role.ADMIN ? DEFAULT_ADMIN_ACCESS : DEFAULT_STAFF_ACCESS));
        }
    }, [user]);
    const handleClose = useCallback(() => {
        if (isSaving)
            return;
        setIsRoleDropdownOpen(false);
        onClose();
    }, [isSaving, onClose]);
    const handleRoleChange = useCallback((newRole) => {
        setSelectedRole(newRole);
        // When changing to Admin, give full access
        if (newRole === Role.ADMIN) {
            setAccess(DEFAULT_ADMIN_ACCESS);
        }
        else if (newRole === Role.STAFF) {
            setAccess(DEFAULT_STAFF_ACCESS);
        }
        setIsRoleDropdownOpen(false);
    }, []);
    const isAdminRole = selectedRole === Role.ADMIN;
    const handleAccessToggle = useCallback((key) => {
        setAccess((prev) => {
            if (isAdminRole && isCoreAccessKey(key)) {
                return prev;
            }
            if (key === "all") {
                const newAllValue = !prev.all;
                const nextAccess = {
                    all: newAllValue,
                    sites: newAllValue,
                    quotes: newAllValue,
                    templates: newAllValue,
                    jobs: newAllValue,
                    contacts: newAllValue,
                    estimating: newAllValue,
                    costSettings: newAllValue,
                    forms: newAllValue,
                    userManagement: newAllValue,
                };
                if (isAdminRole) {
                    CORE_ACCESS_ITEMS.forEach((item) => {
                        nextAccess[item.key] = true;
                    });
                }
                return nextAccess;
            }
            const newAccess = { ...prev, [key]: !prev[key] };
            if (key === "quotes") {
                newAccess.estimating = newAccess.quotes;
            }
            else if (key === "estimating") {
                newAccess.quotes = newAccess.estimating;
            }
            if (isAdminRole) {
                CORE_ACCESS_ITEMS.forEach((item) => {
                    newAccess[item.key] = true;
                });
            }
            const allEnabled = newAccess.contacts &&
                newAccess.sites &&
                newAccess.estimating &&
                newAccess.jobs &&
                newAccess.quotes &&
                newAccess.templates &&
                newAccess.costSettings &&
                newAccess.forms &&
                newAccess.userManagement;
            newAccess.all = allEnabled;
            return newAccess;
        });
    }, [isAdminRole]);
    const handleSave = useCallback(() => {
        if (!user)
            return;
        onSave(user.id, name.trim(), email.trim(), selectedRole, access);
    }, [user, name, email, selectedRole, access, onSave]);
    const getInitials = (userName) => {
        return userName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };
    if (!user)
        return null;
    return (_jsx(Modal, { isOpen: isOpen, onClose: handleClose, children: _jsxs("div", { className: styles.modalWrapper, children: [_jsx("div", { className: styles.modalHeader, children: _jsx("h2", { className: styles.modalTitle, children: "Edit User" }) }), _jsxs("div", { className: styles.userInfoSection, children: [_jsx("div", { className: styles.userProfile, children: _jsx("div", { className: styles.userAvatar, children: getInitials(name || user.name) }) }), _jsxs("div", { className: styles.roleDropdownContainer, children: [_jsxs("button", { type: "button", className: `${styles.roleDropdownToggle} ${styles[getRoleClassName(selectedRole)] || ""}`, onClick: () => setIsRoleDropdownOpen(!isRoleDropdownOpen), children: [_jsx("span", { children: getRoleLabel(selectedRole) }), _jsx("svg", { className: `${styles.roleDropdownArrow} ${isRoleDropdownOpen ? styles.open : ""}`, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M4 6L8 10L12 6", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })] }), isRoleDropdownOpen && (_jsx("div", { className: styles.roleDropdownMenu, children: ROLE_OPTIONS.map((option) => (_jsx("button", { type: "button", className: styles.roleDropdownItem, onClick: () => handleRoleChange(option.value), children: option.label }, option.value))) }))] })] }), _jsxs("div", { className: styles.userDetailsSection, children: [_jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.formLabel, htmlFor: "edit-user-name", children: "Name" }), _jsx("input", { id: "edit-user-name", type: "text", className: styles.formInput, value: name, onChange: (e) => setName(e.target.value) })] }), _jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.formLabel, htmlFor: "edit-user-email", children: "Email" }), _jsx("input", { id: "edit-user-email", type: "email", className: styles.formInput, value: email, onChange: (e) => setEmail(e.target.value) })] })] }), _jsxs("div", { className: styles.accessSection, children: [_jsx("h3", { className: styles.accessTitle, children: "Access" }), _jsxs("div", { className: styles.accessGroup, children: [_jsx("div", { className: styles.accessGroupTitle, children: "Core Access" }), _jsx("div", { className: styles.accessList, children: CORE_ACCESS_ITEMS.map((item) => (_jsxs("div", { className: styles.accessItem, children: [_jsx("span", { className: styles.accessLabel, children: item.label }), _jsxs("label", { className: styles.toggle, children: [_jsx("input", { type: "checkbox", checked: access[item.key], onChange: () => handleAccessToggle(item.key), disabled: isAdminRole }), _jsx("span", { className: styles.toggleSlider })] })] }, item.key))) })] }), isAdminRole && (_jsxs("div", { className: styles.accessGroup, children: [_jsx("div", { className: styles.accessGroupTitle, children: "Admin Access Menu" }), _jsx("div", { className: styles.accessList, children: ADDITIONAL_ACCESS_ITEMS.map((item) => (_jsxs("div", { className: styles.accessItem, children: [_jsx("span", { className: styles.accessLabel, children: item.label }), _jsxs("label", { className: styles.toggle, children: [_jsx("input", { type: "checkbox", checked: access[item.key], onChange: () => handleAccessToggle(item.key) }), _jsx("span", { className: styles.toggleSlider })] })] }, item.key))) })] }))] }), _jsxs("div", { className: styles.modalFooter, children: [_jsx("button", { type: "button", className: styles.cancelBtn, onClick: handleClose, disabled: isSaving, children: "Cancel" }), _jsx("button", { type: "button", className: styles.saveBtn, onClick: handleSave, disabled: isSaving || !name.trim() || !email.trim(), children: isSaving ? "Saving..." : "Save Changes" })] })] }) }));
};
